import { getPresetDefinitionById } from '../business-logic/presets/getPresetDefinitionById'
import { addDynamicItemPage } from '../business-logic/dynamic-pages/add-dynamic-item-page/addDynamicItemPage'
import { createDPErrorHandler, isDpError } from './utils/createDPErrorHandler'
import { addPageWithRepeater } from '../business-logic/dynamic-pages/add-page-with-repeater/addPageWithRepeater'
import { replaceDynamicPageCollection } from '../business-logic/dynamic-pages/replace-dynamic-page-collection/replaceDynamicPageCollection'
import { addCollectionAndCmsPages } from '../business-logic/dynamic-pages/add-collection-and-cms-pages/addCollectionAndCmsPages'
import { EDITOR_TYPE } from '@wix/wix-data-client-common'
import { addCmsPages } from '../business-logic/dynamic-pages/add-cms-pages/addCmsPages'
import { addCollectionAndPageWithRepeater } from '../business-logic/dynamic-pages/add-collection-and-page-with-repeater/addCollectionAndPageWithRepeater'
import { addCollectionAndDynamicItemPage } from '../business-logic/dynamic-pages/add-collection-and-dynamic-item-page/addCollectionAndDynamicItemPage'
import { addDynamicItemPageAndLinkToComponent } from '../business-logic/dynamic-pages/add-dynamic-item-page-and-link-to-component/addDynamicItemPageAndLinkToComponent'

import type {
  AppApi,
  Editor3DynamicPagesAppApi,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import type { HttpClient } from '@wix/http-client'

type Params = {
  editorSdkProxy: EditorSDK
  finalAppApiPromise: Promise<AppApi>
  collectionsApi: CollectionsApi
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  wixData: WixDataApi
  httpClient: HttpClient
}

const editorType = EDITOR_TYPE.EDITOR3

export const getEditor3Api = ({
  editorSdkProxy,
  finalAppApiPromise,
  collectionsApi,
  i18n,
  fedopsLogger,
  logger,
  wixData,
  httpClient,
}: Params): Editor3DynamicPagesAppApi => {
  const withDPErrorHandler = <T>(fn: () => Promise<T>) =>
    createDPErrorHandler<T>({
      i18n,
      logger,
      editorSdkProxy,
    })(fn)

  return {
    // Note: This method expects the router to have a single route
    async replaceDynamicPageCollection({
      pageRef,
      newCollectionId,
      newRouterPrefix,
      fieldMap,
    }) {
      return withDPErrorHandler(async () => {
        return replaceDynamicPageCollection({
          pageRef,
          newCollectionId,
          newRouterPrefix,
          fieldMap,
          editorSdkProxy,
          collectionsApi,
          appApi: await finalAppApiPromise,
          httpClient,
          i18n,
          fedopsLogger,
        })
      })
    },

    async addCollectionAndCmsPages({ presetId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addCollectionAndCmsPages({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          collectionsApi,
          preset,
          logger,
          fedopsLogger,
          wixDataApi: wixData,
          i18n,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addPageWithRepeater({ presetId, existingCollectionId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addPageWithRepeater({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          preset,
          collectionsApi,
          existingCollectionId,
          fedopsLogger,
          i18n,
          logger,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addDynamicItemPage({ presetId, existingCollectionId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addDynamicItemPage({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          preset,
          collectionsApi,
          existingCollectionId,
          i18n,
          fedopsLogger,
          logger,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addCmsPages({ presetId, existingCollectionId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addCmsPages({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          preset,
          collectionsApi,
          existingCollectionId,
          i18n,
          fedopsLogger,
          logger,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addCollectionAndPageWithRepeater({ presetId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addCollectionAndPageWithRepeater({
          editorSdkProxy,
          preset,
          collectionsApi,
          wixDataApi: wixData,
          logger,
          i18n,
          fedopsLogger,
          appApi: await finalAppApiPromise,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addCollectionAndDynamicItemPage({ presetId, biParams }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addCollectionAndDynamicItemPage({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          collectionsApi,
          wixDataApi: wixData,
          i18n,
          fedopsLogger,
          logger,
          preset,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },

    async addDynamicItemPageAndLinkToComponent({
      presetId,
      existingCollectionId,
      componentRef,
      controllerRef,
      biParams,
    }) {
      const preset = await getPresetDefinitionById(
        presetId,
        editorType,
        editorSdkProxy,
      )

      const result = await withDPErrorHandler(async () => {
        return addDynamicItemPageAndLinkToComponent({
          editorSdkProxy,
          appApi: await finalAppApiPromise,
          preset,
          collectionsApi,
          i18n,
          fedopsLogger,
          logger,
          existingCollectionId,
          componentRef,
          controllerRef,
          biParams,
        })
      })

      if (isDpError(result)) {
        throw result.error
      }

      return result
    },
  }
}
